/**
 * x-hotspot directive.
 *
 * Manages a hotspot dialog that floats over an image. The dialog
 * position is calculated dynamically using fixed coordinates derived from the
 * trigger button's bounding rect, preferring placement to the right or left of
 * the trigger with vertical centering, and falling back to above/below for
 * horizontally-centred placement.
 *
 * On desktop the dialog opens on hover (with a 120 ms delay) and closes when
 * the pointer leaves both the trigger and the dialog. On touch devices, the
 * browser's default link/action is prevented instead, leaving navigation to
 * the host page's own touch handling. The dialog also closes automatically on
 * scroll and resize events because the trigger's position changes.
 *
 * Requires `$refs.dialog` (the `<dialog>` element) and `$refs.trigger` (the
 * trigger button) inside the directive element.
 *
 * @example
 *   <div x-hotspot>
 *     <button x-ref="trigger" type="button">+</button>
 *     <dialog x-ref="dialog">…product card…</dialog>
 *   </div>
 */
(() => {
  document.addEventListener("alpine:init", () => {
    // Registry of every hotspot instance on the page, keyed by root element,
    // so opening one can close any others left open.
    const hotspots = new Map();

    Alpine.directive("hotspot", (el) => {
      let timer;

      Alpine.bind(el, () => ({
        ["x-data"]() {
          return {
            /**
             * Binds `this` context to methods that are registered as event
             * listeners so they can be removed correctly on teardown.
             */
            init() {
              this.lightDismissMouse = this.lightDismissMouse.bind(this);
              this.lightDismissKeyboard = this.lightDismissKeyboard.bind(this);
              this.handleScroll = this.handleScroll.bind(this);

              hotspots.set(this.$root, this);
            },

            /**
             * Shows the dialog after a 120 ms delay when the pointer enters the
             * trigger. Skips if the dialog is already open.
             */
            handlePointerEnter() {
              const { dialog } = this.$refs;

              if (dialog.open) return;

              timer = setTimeout(() => {
                this.showDialog();
              }, 120);
            },

            /**
             * Closes the dialog when the pointer leaves either the trigger or
             * the dialog and is not moving into the other element. Also clears
             * the pending open timer if the pointer leaves before it fires.
             * @param {PointerEvent} e - The native pointerleave event.
             */
            handlePointerLeave(e) {
              const { dialog, trigger } = this.$refs;

              // Clear the open timer if leaving the trigger before the dialog opens.
              if (timer) {
                clearTimeout(timer);
                timer = null;
              }

              if (!dialog.open) return;

              const isLeavingTrigger = e.target === trigger;
              const isLeavingDialog = e.target === dialog;
              const isGoingToDialog =
                e.relatedTarget === dialog ||
                (e.relatedTarget instanceof Element &&
                  e.relatedTarget.closest("dialog") === dialog);
              const isGoingToTrigger = e.relatedTarget === trigger;

              if (
                (isLeavingTrigger && !isGoingToDialog) ||
                (isLeavingDialog && !isGoingToTrigger)
              ) {
                this.closeDialog();
              }
            },

            /**
             * Calculates the dialog's fixed position relative to the trigger button
             * and the hotspot container. The dialog is positioned to the right,
             * left, or centred horizontally depending on available space. Vertical
             * position is centred on the trigger for left/right placement, or
             * below/above for centred placement. All coordinates are clamped to
             * the viewport bounds.
             * @returns {Promise<void>|undefined} A double-rAF promise that resolves
             *   after layout and paint, or undefined when the container is missing.
             */
            calculateDialogPlacement() {
              const { trigger, dialog } = this.$refs;

              const hotspotsContainer = this.$root.parentElement;

              if (!hotspotsContainer) {
                return;
              }

              // Gap between the trigger button and the dialog edge.
              const BUTTON_GAP = 16;
              // Minimum gap between the dialog and the viewport edges.
              const VIEWPORT_GAP = 8;

              // Get rects in viewport coordinates.
              const containerRect = hotspotsContainer.getBoundingClientRect();
              const triggerRect = trigger.getBoundingClientRect();

              // Temporarily show the dialog invisibly to measure its dimensions.
              dialog.style.visibility = "hidden";
              dialog.style.display = "block";
              dialog.style.transform = "none";
              dialog.removeAttribute("data-placement");

              const { width: dialogWidth, height: dialogHeight } =
                dialog.getBoundingClientRect();

              // Reset temporary measurement styles.
              dialog.style.removeProperty("display");
              dialog.style.removeProperty("visibility");
              dialog.style.removeProperty("transform");

              // Calculate available space relative to the container.
              const buttonLeft = triggerRect.left - containerRect.left;
              const buttonRight = triggerRect.right - containerRect.left;
              const spaceRight = containerRect.width - buttonRight;
              const spaceLeft = buttonLeft;

              // Determine horizontal placement.
              let x = "right";

              if (spaceRight >= dialogWidth + BUTTON_GAP) {
                x = "right";
              } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
                x = "left";
              } else {
                x = "center";
              }

              // Compute fixed coordinates.
              let top, left;
              let y = "bottom";

              // Horizontal position.
              if (x === "right") {
                left = triggerRect.right + BUTTON_GAP;
              } else if (x === "left") {
                left = triggerRect.left - BUTTON_GAP - dialogWidth;
              } else {
                left =
                  triggerRect.left + triggerRect.width / 2 - dialogWidth / 2;
              }

              // Vertical position.
              if (x !== "center") {
                // For left/right: vertically centre the dialog on the trigger.
                top =
                  triggerRect.top + triggerRect.height / 2 - dialogHeight / 2;

                if (top + dialogHeight > containerRect.bottom - VIEWPORT_GAP) {
                  // Overflows container bottom — align bottom of dialog with bottom of trigger.
                  top = triggerRect.bottom - dialogHeight;
                  y = "top";

                  // If still overflows container top, clamp to container top.
                  if (top < containerRect.top + VIEWPORT_GAP) {
                    top = containerRect.top + VIEWPORT_GAP;
                  }
                } else if (top < containerRect.top + VIEWPORT_GAP) {
                  // Overflows container top — clamp to container top.
                  top = containerRect.top + VIEWPORT_GAP;
                }
              } else {
                // Centre horizontal: position below or above the trigger.
                const spaceBelow = containerRect.bottom - triggerRect.bottom;
                const spaceAbove = triggerRect.top - containerRect.top;

                if (spaceBelow >= dialogHeight + BUTTON_GAP) {
                  top = triggerRect.bottom + BUTTON_GAP;
                  y = "bottom";
                } else if (spaceAbove >= dialogHeight + BUTTON_GAP) {
                  top = triggerRect.top - BUTTON_GAP - dialogHeight;
                  y = "top";
                } else {
                  // Choose whichever side has more space.
                  if (spaceBelow >= spaceAbove) {
                    top = triggerRect.bottom + BUTTON_GAP;
                    y = "bottom";
                  } else {
                    top = triggerRect.top - BUTTON_GAP - dialogHeight;
                    y = "top";
                  }
                }
              }

              // Clamp to viewport.
              left = Math.max(
                VIEWPORT_GAP,
                Math.min(left, window.innerWidth - dialogWidth - VIEWPORT_GAP),
              );
              top = Math.max(
                VIEWPORT_GAP,
                Math.min(top, window.innerHeight - dialogHeight - VIEWPORT_GAP),
              );

              // Apply fixed position.
              dialog.style.top = `${top}px`;
              dialog.style.left = `${left}px`;
              dialog.dataset.placement = `${x},${y}`;

              // Double-rAF ensures layout and paint are complete before resolving.
              return new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve)),
              );
            },

            /**
             * Handles a hotspot click. Closes the dialog if it is already open.
             * On touch devices, prevents the default action instead of opening
             * the dialog (touch interaction is handled elsewhere).
             * On desktop, opens the dialog.
             * @param {MouseEvent} e - The click event.
             */
            handleHotspotClick(e) {
              if (this.$refs.dialog.open) {
                this.closeDialog();
                return;
              }

              // On touch devices, prevent default and let the host page handle the interaction.
              if (this.$app.$isTouchDevice()) {
                e.preventDefault();
                e.stopPropagation();
              } else {
                this.showDialog();
              }
            },

            /**
             * Removes all document/window event listeners registered during
             * `showDialog`, plus this instance's entry in the `hotspots` registry.
             */
            destroy() {
              document.body.removeEventListener(
                "click",
                this.lightDismissMouse,
              );
              document.body.removeEventListener(
                "keydown",
                this.lightDismissKeyboard,
              );
              document.body.removeEventListener(
                "keyup",
                this.lightDismissKeyboard,
              );
              window.removeEventListener("scroll", this.handleScroll, {
                capture: true,
              });
              window.removeEventListener("resize", this.handleScroll);
              hotspots.delete(this.$root);
            },

            /**
             * Calculates dialog placement, then shows the dialog and attaches
             * light-dismiss and scroll listeners. Dispatches `hotspot-dialog-open`.
             * Closes any other hotspot dialogs left open on the page.
             * @returns {Promise<void>}
             */
            async showDialog() {
              const { dialog } = this.$refs;

              hotspots.forEach((hotspot) => {
                if (hotspot !== this && hotspot.$refs.dialog.open) {
                  hotspot.closeDialog();
                }
              });

              await this.calculateDialogPlacement();

              this.$root.dataset.dialogOpen = "";
              dialog.dataset.showing = "true";
              dialog.show();

              this.$dispatch("hotspot-dialog-open");

              document.body.addEventListener("click", this.lightDismissMouse);
              document.body.addEventListener(
                "keydown",
                this.lightDismissKeyboard,
              );
              document.body.addEventListener(
                "keyup",
                this.lightDismissKeyboard,
              );
              window.addEventListener("scroll", this.handleScroll, {
                passive: true,
                capture: true,
              });
              window.addEventListener("resize", this.handleScroll);
            },

            /**
             * Closes the dialog, removes event listeners, and waits for any CSS
             * animations to complete before cleaning up dataset attributes.
             * Dispatches `hotspot-dialog-close`.
             * @returns {Promise<void>}
             */
            async closeDialog() {
              const { dialog } = this.$refs;

              delete this.$root.dataset.dialogOpen;
              dialog.dataset.closing = "true";

              dialog.close();

              this.$dispatch("hotspot-dialog-close");

              document.body.removeEventListener(
                "click",
                this.lightDismissMouse,
              );
              document.body.removeEventListener(
                "keydown",
                this.lightDismissKeyboard,
              );
              document.body.removeEventListener(
                "keyup",
                this.lightDismissKeyboard,
              );
              window.removeEventListener("scroll", this.handleScroll, {
                capture: true,
              });
              window.removeEventListener("resize", this.handleScroll);

              // Wait for CSS transitions to complete before removing data attributes.
              const animations = dialog.getAnimations({ subtree: true });

              await Promise.allSettled(animations.map((a) => a.finished));

              if (!dialog.open) {
                delete dialog.dataset.showing;
                delete dialog.dataset.closing;
                delete dialog.dataset.placement;
                dialog.style.removeProperty("top");
                dialog.style.removeProperty("left");
                dialog.style.removeProperty("width");
              }
            },

            /**
             * Closes the dialog when a scroll event is detected, since the
             * trigger button moves with the page.
             */
            handleScroll() {
              const { dialog } = this.$refs;

              if (dialog.open) {
                this.closeDialog();
              }
            },

            /**
             * Closes the dialog when a click outside of it is detected.
             * @param {MouseEvent} event - The click event.
             */
            lightDismissMouse(event) {
              const { dialog } = this.$refs;

              if (this.$app.$isClickedOutside(event, dialog)) {
                this.closeDialog();
              }
            },

            /**
             * Closes the dialog on Escape keydown or when focus leaves the dialog
             * on keyup.
             * @param {KeyboardEvent} event - The keyboard event.
             */
            lightDismissKeyboard(event) {
              const { dialog } = this.$refs;
              if (
                (event.type === "keydown" && event.key === "Escape") ||
                (event.type === "keyup" &&
                  !dialog.matches(":is(:focus, :focus-visible, :focus-within)"))
              ) {
                this.closeDialog();
              }
            },
          };
        },
      }));
    });
  });
})();
